/**
 * The per-page-load shuffle.
 *
 * Every grid and showcase row nudges close scores apart by a hash of
 * (listing id, seed), so a reload shows a different order while the best
 * matches stay near the top. One seed gives one fixed order, so page 2 still
 * continues page 1 and Back refetches the same pages.
 *
 * The seed is one of SHUFFLE_SEED_COUNT values, not a free number: the
 * CDN-cached routes key on the URL, and 16 variants keep their hit rate.
 *
 * `shuffleUnit` must stay bit-identical to the painless in
 * buildRelevanceScoreScript (utils/elasticsearch/query-builders.ts): both use
 * 32-bit integer multiplies that wrap the same way in Java and in Math.imul.
 */

export const SHUFFLE_SEED_COUNT = 16;

/** The wobble the ranking script applies when the dial is unset: +-5% of the score. */
export const DEFAULT_SHUFFLE_JITTER = 0.05;

/** Above this a factor could reach 0 or flip order wholesale. */
export const MAX_SHUFFLE_JITTER = 0.5;

/**
 * How far an item in an unscored list (ids in a fixed order, merged rows) may
 * move: its position plus up to this many places. 3 lets neighbours trade
 * places without the 10th item reaching the top.
 */
export const DEFAULT_SHUFFLE_WINDOW = 3;

// Hash constants as signed 32-bit ints, so the painless source can use them as
// plain int literals: 0x9E3779B1, 0x85EBCA77, 0x2C1B3C6D.
const K1 = -1640531535;
const K2 = -2048144777;
const K3 = 739982445;

/** A seed from a URL param or cookie, or undefined for anything that is not one. */
export function parseShuffleSeed(raw: unknown): number | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  if (typeof value === 'string' && !/^\d+$/.test(value)) return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n < SHUFFLE_SEED_COUNT ? n : undefined;
}

/** 0 <= u < 1, fixed for one (listing, seed). Mirrors the painless exactly. */
export function shuffleUnit(listingId: number, seed: number): number {
  let h = Math.imul(listingId | 0, K1) ^ Math.imul((seed | 0) + 1, K2);
  h ^= h >>> 15;
  h = Math.imul(h, K3);
  h ^= h >>> 13;
  return (h >>> 8) / 16777216;
}

/** The score multiplier: 1 +- jitter. Exactly 1 with no seed or no jitter. */
export function shuffleFactor(listingId: number | null | undefined, seed: number | undefined, jitter: number): number {
  if (seed === undefined || !(jitter > 0) || listingId === null || listingId === undefined || !Number.isFinite(listingId)) return 1;
  return 1 + jitter * (2 * shuffleUnit(listingId, seed) - 1);
}

/**
 * Reorder a list that has no scores: each item keeps its place plus up to
 * `window` places, decided by the seed. Returns the same order with no seed.
 * Items whose id is not a number keep their exact place in the key order.
 */
export function jitterOrder<T>(
  items: readonly T[],
  idOf: (item: T) => unknown,
  seed: number | undefined,
  window: number = DEFAULT_SHUFFLE_WINDOW
): T[] {
  if (seed === undefined || window <= 0 || items.length < 2) return [...items];
  return items
    .map((item, index) => {
      const id = Number(idOf(item));
      const nudge = Number.isFinite(id) ? shuffleUnit(id, seed) * window : 0;
      return { item, key: index + nudge };
    })
    .sort((a, b) => a.key - b.key)
    .map((entry) => entry.item);
}

let pageLoadSeed: number | undefined;
let pageLoadAt: number | undefined;
/** Each list's `at`, keyed by what names the list: see listAtFor. */
const listAts = new Map<string, { at: number; askedMs: number }>();

/**
 * This page load's seed, made once per hard load (a reload makes a new one).
 * Browser only: on the server a module-level value would be shared by every
 * request, so it answers undefined there and the server reads the request's.
 */
export function getPageLoadShuffleSeed(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  if (pageLoadSeed === undefined) pageLoadSeed = Math.floor(Math.random() * SHUFFLE_SEED_COUNT);
  return pageLoadSeed;
}

/** Add this page load's `seed` to a request's params. No-op on the server. */
export function appendShuffleSeed(params: URLSearchParams): URLSearchParams {
  const seed = getPageLoadShuffleSeed();
  if (seed !== undefined && !params.has('seed')) params.set('seed', String(seed));
  return params;
}

/**
 * This page load's start, in whole seconds, made once per hard load like the
 * seed. Only tag fallback still sends it: it continues the feed it sits under
 * and excludes what is on screen itself. Lists send their own `at`
 * (listAtFor). Browser only.
 */
export function getPageLoadAt(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  if (pageLoadAt === undefined) pageLoadAt = Math.floor(Date.now() / 1000);
  return pageLoadAt;
}

/** Params that say which page of a list, or travel with every request; the rest name the list. */
const NOT_THE_LIST = new Set(['page', 'offset', 'limit', 'at', 'seed']);
/**
 * How long a list keeps its `at`. Back to it within this ranks it exactly as
 * before; after it, the list counts everything seen since, like a new one.
 */
export const LIST_AT_REUSE_MS = 30 * 60_000;
const LIST_AT_MAX = 200;

/**
 * This list's `at`: the moment it was first asked for in this page load, in
 * whole seconds. Every later page of it, and Back to it, sends the same one,
 * so "already seen" (utils/seen-listings.ts) holds still within the list.
 *
 * WHY PER LIST (2026-10-30). It was one `at` per hard load, and the site is a
 * single-page app: every search, category and listing opened after landing
 * shared it, so nothing seen since landing counted as seen. On prod 21% of
 * browse and search's first dozen was a repeat for that visitor, 58% of them
 * within 2 minutes of the first showing. Browser only.
 */
export function listAtFor(params: URLSearchParams, nowMs: number = Date.now()): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const key = [...params.entries()].filter(([k]) => !NOT_THE_LIST.has(k)).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, v]) => `${k}=${v}`).join('&');
  const known = listAts.get(key);
  if (known && nowMs - known.askedMs < LIST_AT_REUSE_MS) return known.at;
  const at = Math.floor(nowMs / 1000);
  if (listAts.size >= LIST_AT_MAX) listAts.delete(listAts.keys().next().value as string);
  listAts.set(key, { at, askedMs: nowMs });
  return at;
}

/**
 * Add this list's `at` (listAtFor) to a request's params. No-store routes
 * only: on a CDN-cached route a per-list value would split the cache.
 */
export function appendPageLoadAt(params: URLSearchParams): URLSearchParams {
  if (params.has('at')) return params;
  const at = listAtFor(params);
  if (at !== undefined) params.set('at', String(at));
  return params;
}

/** For test isolation only. */
export function resetPageLoadShuffleSeedForTests(): void {
  pageLoadSeed = undefined;
  pageLoadAt = undefined;
  listAts.clear();
}

/**
 * `{ shuffleSeed }` from a request's `?seed=`, or `{}` without one. Spread into
 * the SearchOptions of every query a grid or row runs.
 */
export function shuffleSeedOption(req: { query?: Record<string, unknown> }): { shuffleSeed?: number } {
  const seed = parseShuffleSeed(req.query?.seed);
  return seed === undefined ? {} : { shuffleSeed: seed };
}
